/* ============================================================
   Talention — Módulo Igualdad · Cliente HTTP, adaptador y CSV
   ============================================================ */

/* --- Motor ---
   Cliente HTTP de la API (Laravel /api/v1).
   Envoltura ligera sobre fetch: token JWT, query params, JSON y multipart,
   manejo de 401 y descargas de ficheros (export Excel/Word/PDF). */

const TOKEN_KEY = "ig_token";
// Mismo origen en Docker (el gateway nginx sirve el SPA y proxya /api). En
// `npm run dev` lo resuelve el proxy de Vite (ver vite.config.js).
const BASE = import.meta.env.VITE_API_BASE || "/api/v1";

const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

let onUnauthorized = null;
const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

class ApiError extends Error {
  constructor(message, status, errors) {
    super(message || "Error de red");
    this.name = "ApiError";
    this.status = status;
    this.errors = errors || null;
  }
}

function qs(params) {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    sp.append(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

function authHeaders(extra) {
  const h = { Accept: "application/json", ...(extra || {}) };
  const t = getToken();
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

async function request(method, path, { params, body, form } = {}) {
  let payload;
  let headers;
  if (form) {
    // FormData: el navegador fija el Content-Type con su boundary.
    payload = form;
    headers = authHeaders();
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
    headers = authHeaders({ "Content-Type": "application/json" });
  } else {
    headers = authHeaders();
  }

  let res;
  try {
    res = await fetch(BASE + path + qs(params), { method, headers, body: payload });
  } catch (e) {
    throw new ApiError("No se pudo conectar con el servidor.", 0);
  }

  if (res.status === 401) {
    setToken(null);
    if (onUnauthorized) onUnauthorized();
    throw new ApiError("Sesión expirada. Vuelve a iniciar sesión.", 401);
  }
  if (res.status === 204) return null;

  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (data && data.message) || `Error ${res.status}`;
    throw new ApiError(msg, res.status, data && data.errors);
  }
  return data;
}

const apiGet = (path, params) => request("GET", path, { params });
const apiPost = (path, body, opts) => request("POST", path, { body, ...opts });
const apiPut = (path, body) => request("PUT", path, { body });
const apiPatch = (path, body) => request("PATCH", path, { body });
// Algunas bajas exigen cuerpo (p. ej. anular una respuesta de valoracion pide
// `motivo`); los llamantes que no lo necesitan siguen pasando solo la ruta.
const apiDelete = (path, body) => request("DELETE", path, body === undefined ? {} : { body });

/* --- Multipart --- */

/* Vuelca un valor en el FormData con la notación de corchetes que PHP entiende
   como array asociativo: `mapeo[SAL_BASE]=concepto:Salario base`. Serializarlo
   como JSON en un solo campo lo hacía llegar al backend como una cadena, y una
   regla de validación `array` lo rechazaba. Anida por si algún día hace falta. */
function volcarCampo(fd, clave, valor) {
  if (valor === null || valor === undefined) return;

  if (valor instanceof File || valor instanceof Blob) { fd.append(clave, valor); return; }

  if (Array.isArray(valor)) {
    valor.forEach((v, i) => volcarCampo(fd, `${clave}[${i}]`, v));
    return;
  }

  if (typeof valor === "object") {
    for (const [k, v] of Object.entries(valor)) volcarCampo(fd, `${clave}[${k}]`, v);
    return;
  }

  // Un booleano suelto llega como "true"/"false", que Laravel no interpreta.
  fd.append(clave, typeof valor === "boolean" ? (valor ? "1" : "0") : valor);
}

/** POST multipart (subida de fichero). `fields` = objeto plano; `files` = {campo: File}. */
function apiUpload(path, fields = {}, files = {}) {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) volcarCampo(fd, k, v);
  for (const [k, f] of Object.entries(files)) if (f) fd.append(k, f);
  return request("POST", path, { form: fd });
}

/* --- Paginación --- */

/** GET que recorre toda la paginación y concatena `data` de todas las páginas. */
async function apiGetAll(path, params = {}) {
  const first = await apiGet(path, { per_page: 100, ...params, page: 1 });
  const items = [...(first.data || [])];
  const last = first.meta?.last_page || 1;
  for (let page = 2; page <= last; page++) {
    const next = await apiGet(path, { per_page: 100, ...params, page });
    items.push(...(next.data || []));
  }
  return items;
}

/* --- Descargas --- */

/** Descarga un fichero (export) respetando el nombre del Content-Disposition. */
async function apiDownload(path, params, fallbackName = "descarga") {
  const res = await fetch(BASE + path + qs(params), { headers: authHeaders() });
  if (res.status === 401) {
    setToken(null);
    if (onUnauthorized) onUnauthorized();
    throw new ApiError("Sesión expirada.", 401);
  }
  if (!res.ok) {
    let msg = `Error ${res.status}`;
    try { const j = await res.json(); msg = j.message || msg; } catch (_) { /* binario */ }
    throw new ApiError(msg, res.status);
  }
  const cd = res.headers.get("content-disposition") || "";
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  const name = match ? decodeURIComponent(match[1].replace(/"/g, "")) : fallbackName;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* --- Adaptador ---
   Adaptadores entre la forma de la API (snake_case, relaciones anidadas) y la
   forma que esperan las vistas heredadas (camelCase, nombres planos). */

function mapPersona(p) {
  const puesto = p.puesto || {};
  return {
    id: p.id,
    codigo: p.codigo,
    nombre: p.nombre,
    sexo: p.sexo,
    puesto: puesto.nombre || "—",
    bench: puesto.bench || "",
    pts: puesto.pts ?? 0,
    depto: p.departamento?.nombre || "—",
    centro: p.centro?.nombre || "—",
    razon: p.razon,
    jornada: Number(p.jornada) || 0,
    fijo100: Number(p.fijo_anual) || 0,
    variable100: Number(p.variable_anual) || 0,
    total100: Number(p.total_anual) || 0,
    fijoReal: Number(p.fijo_real) || 0,
    totalReal: Number(p.total_real) || 0,
    cotaInf: Number(p.cota_inf) || 0,
    cotaMed: Number(p.cota_med) || 0,
    cotaSup: Number(p.cota_sup) || 0,
    posBanda: Number(p.pos_banda) || 0,
    desfase: Number(p.desfase) || 0,
    grupo: p.grupo,
    tiv: p.tiv,
  };
}

/* --- Generador CSV ---
   Generación de CSV en el navegador para los botones "Excel" de las vistas que
   no tienen endpoint de exportación dedicado (el registro retributivo sí lo
   tiene en el backend). Usa ';' y BOM para que Excel en español lo abra bien. */

function downloadCsv(filename, header, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const sep = ";";
  const lines = [header.map(esc).join(sep), ...rows.map((r) => r.map(esc).join(sep))];
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export {
  getToken,
  setToken,
  setUnauthorizedHandler,
  ApiError,
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  apiUpload,
  apiGetAll,
  apiDownload,
  mapPersona,
  downloadCsv,
};
